import React, { useState, useEffect, useRef } from 'react';
import { Image as ImageIcon } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { uploadMedia } from '../../lib/storage';
import { ConversationComment } from './ConversationComment';
import { UserMentionInput } from '../ui/UserMentionInput';
import { GifIcon } from '../ui/GifIcon';
import { GifPicker } from '../ui/GifPicker';
import type { ConversationComment as ConversationCommentType } from '../../types/conversations';
import { toast } from 'sonner';

const MAX_COMMENT_MEDIA = 4;

export function ConversationCommentSection({
  postId,
  onCommentAdded,
  onCommentDeleted
}: {
  postId: string;
  onCommentAdded: () => void;
  onCommentDeleted?: () => void;
}) {
  const [comments, setComments] = useState<ConversationCommentType[]>([]);
  const [newComment, setNewComment] = useState('');
  const [gifUrls, setGifUrls] = useState<string[]>([]);
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mentionedUsers, setMentionedUsers] = useState<string[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('users')
          .select('username, avatar_url')
          .eq('id', user.id)
          .single();
        setCurrentUser({ id: user.id, username: profile?.username, avatar_url: profile?.avatar_url });
      }
    };
    getUser();
  }, []);

  const fetchComments = async () => {
    try {
      const { data, error } = await supabase
        .from('conversation_comments')
        .select(`id, post_id, parent_id, user_id, content, images, created_at, user:users!conversation_comments_user_id_fkey (username, avatar_url)`)
        .eq('post_id', postId)
        .order('created_at', { ascending: true });
      if (error) throw error;

      const commentMap = new Map<string, ConversationCommentType>();
      const rootComments: ConversationCommentType[] = [];
      data?.forEach((c) => commentMap.set(c.id, { ...c, replies: [] }));
      data?.forEach((c) => {
        const withReplies = commentMap.get(c.id)!;
        if (c.parent_id) {
          commentMap.get(c.parent_id)?.replies.push(withReplies);
        } else {
          rootComments.push(withReplies);
        }
      });
      setComments(rootComments);
    } catch (err) {
      console.error('Error fetching comments:', err);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [postId]);

  const totalMedia = gifUrls.length + mediaFiles.length;
  const canAddMedia = totalMedia < MAX_COMMENT_MEDIA;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter(
      (f) => f.type.startsWith('image/') || f.type.startsWith('video/')
    );
    if (files.length + totalMedia > MAX_COMMENT_MEDIA) {
      toast.error(`Maximum ${MAX_COMMENT_MEDIA} images/videos allowed`);
      return;
    }
    for (const f of files) {
      if (f.type.startsWith('video/') && f.size > 20 * 1024 * 1024) {
        toast.error('Videos must be under 20MB');
        return;
      }
      if (f.type.startsWith('image/') && f.size > 10 * 1024 * 1024) {
        toast.error('Images must be under 10MB');
        return;
      }
    }
    setMediaFiles((p) => [...p, ...files]);
    e.target.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!newComment.trim() && gifUrls.length === 0 && mediaFiles.length === 0) || !currentUser) return;

    setLoading(true);
    try {
      let mediaUrls: string[] = [...gifUrls];
      if (mediaFiles.length > 0) {
        const uploaded = await Promise.all(
          mediaFiles.map((f) => {
            const bucket = f.type.startsWith('video/') ? 'post-videos' : 'post-images';
            return uploadMedia(f, bucket);
          })
        );
        mediaUrls = [...mediaUrls, ...uploaded.filter(Boolean) as string[]];
      }

      const { error } = await supabase.from('conversation_comments').insert([{
        post_id: postId,
        content: newComment.trim() || '',
        images: mediaUrls,
        user_id: currentUser.id
      }]);

      if (error) throw error;

      setNewComment('');
      setGifUrls([]);
      setMediaFiles([]);
      setMentionedUsers([]);
      await fetchComments();
      onCommentAdded();
    } catch (err: any) {
      console.error('Error adding comment:', err);
      toast.error(err.message || 'Failed to post comment');
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="space-y-4">
        {/* Login prompt at top */}
        <div className="py-4 border-b border-gray-100">
          <div className="text-center py-4">
            <p className="text-gray-600 mb-3">Join the conversation!</p>
            <p className="text-sm text-gray-500 mb-4">Log in or create a free account to comment and reply.</p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => {
                  sessionStorage.setItem('post_onboarding_redirect', `${window.location.pathname}${window.location.search}`);
                  window.dispatchEvent(new CustomEvent('show-auth'));
                }}
                className="px-6 py-2.5 bg-[#8DA847] text-white font-medium rounded-xl hover:bg-[#8DA847]/90 transition-colors"
              >
                Log In
              </button>
              <button
                onClick={() => {
                  sessionStorage.setItem('post_onboarding_redirect', `${window.location.pathname}${window.location.search}`);
                  window.dispatchEvent(new CustomEvent('show-auth'));
                }}
                className="px-6 py-2.5 bg-white text-[#8DA847] font-medium rounded-xl border-2 border-[#8DA847] hover:bg-[#8DA847]/5 transition-colors"
              >
                Create Free Account
              </button>
            </div>
          </div>
        </div>

        {comments.map((c) => (
          <ConversationComment
            key={c.id}
            comment={c}
            onReplyAdded={fetchComments}
            onCommentDeleted={() => {
              fetchComments();
              onCommentDeleted?.();
            }}
            showReplyButton={false}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {/* Comment form at top - X style */}
      <form onSubmit={handleSubmit} className="pb-4 border-b border-gray-100">
        <UserMentionInput
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          onMentionedUsersChange={setMentionedUsers}
          placeholder="Post your reply..."
          className="w-full bg-transparent border-0 px-0 py-2 text-base focus:outline-none focus:ring-0 resize-none min-h-[60px]"
          rows={2}
        />

        {(gifUrls.length > 0 || mediaFiles.length > 0) && (
          <div className="flex flex-wrap gap-2 px-4 pb-2">
            {gifUrls.map((url, i) => (
              <div key={`gif-${i}`} className="relative">
                <img src={url} alt="GIF" className="h-14 w-14 object-cover rounded-lg" />
                <button
                  type="button"
                  onClick={() => setGifUrls((p) => p.filter((_, j) => j !== i))}
                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px]"
                >
                  ×
                </button>
              </div>
            ))}
            {mediaFiles.map((file, i) => (
              <div key={i} className="relative">
                {file.type.startsWith('video/') ? (
                  <video src={URL.createObjectURL(file)} className="h-14 w-14 object-cover rounded-lg" muted loop />
                ) : (
                  <img src={URL.createObjectURL(file)} alt="" className="h-14 w-14 object-cover rounded-lg" />
                )}
                <button
                  type="button"
                  onClick={() => setMediaFiles((p) => p.filter((_, j) => j !== i))}
                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px]"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between px-4 pb-4">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setShowGifPicker(true)}
              disabled={!canAddMedia}
              className="p-2 text-gray-500 hover:text-accent-text hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Add GIF"
            >
              <GifIcon className="h-5 w-5 text-current" />
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={!canAddMedia}
              className="p-2 text-gray-500 hover:text-accent-text hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Add photo or video"
            >
              <ImageIcon className="h-5 w-5" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
          <button
            type="submit"
            disabled={loading || (!newComment.trim() && gifUrls.length === 0 && mediaFiles.length === 0)}
            className="px-6 py-2 text-sm font-medium text-white bg-accent-text rounded-lg hover:bg-accent-text/90 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Posting...' : 'Post'}
          </button>
        </div>
      </form>

      {/* Comments list */}
      <div className="pt-4 space-y-0">
        {comments.map((c) => (
          <ConversationComment
            key={c.id}
            comment={c}
            onReplyAdded={fetchComments}
            onCommentDeleted={() => {
              fetchComments();
              onCommentDeleted?.();
            }}
          />
        ))}
      </div>

      <GifPicker
        isOpen={showGifPicker}
        onClose={() => setShowGifPicker(false)}
        onSelect={(url) => canAddMedia && setGifUrls((p) => [...p, url])}
      />
    </div>
  );
}
